import React, { useEffect, useState, useRef } from "react"
import { graphql } from "gatsby"
import { useTranslation } from "@zestia/gatsby-plugin-react-i18next"
import findImage from "@/utils/find-image"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import WistiaVideoEmbed from "@/components/WistiaVideoEmbed"
import SocialIconTwitter from "@/components/SocialIcon/Twitter"
import SocialIconLinkedIn from "@/components/SocialIcon/LinkedIn"
import SocialIconGithub from "@/components/SocialIcon/Github"
import SocialIconDribbble from "@/components/SocialIcon/Dribbble"
import SocialIconWebsite from "@/components/SocialIcon/Website"
import FeatureTileGrid from "@/components/FeatureTileGrid"
import FeatureTile from "@/components/FeatureTile"
import useIsIntersecting from "@/hooks/is-intersecting"
import useHeadTranslation from "@/hooks/use-head-translation"
import CalloutBox from "@/components/CalloutBox"
import Divider from "@/components/Divider"

import {
  LuMessageSquareQuote,
  LuUsersRound,
  LuHeartHandshake,
  LuPartyPopper,
  LuHandHeart,
} from "react-icons/lu"

export const Head = props => {
  const { t } = useHeadTranslation(props)
  return (
    <SharedHead
      {...props}
      title={t("about.title")}
      description={t("about.description")}
    />
  )
}

const AboutPage = ({ data }) => {
  const { t } = useTranslation()

  const imageGrid = useRef(null)
  const isIntersectingImageGrid = useIsIntersecting(imageGrid)

  const [teamMembers, setTeamMembers] = useState([])

  useEffect(() => {
    setTeamMembers(
      data.teamMembers.edges
        .map(edge => edge.node)
        .sort(() => 0.5 - Math.random()),
    )
  }, [])

  return (
    <MainLayout
      trackingData={{
        intent: "bofu",
      }}
    >
      <section className="hero bg-bottom mt-20 mb-16 container-fluid row center-xs">
        <div className="col-xs-12 col-sm-8">
          <span className="heading-kicker heading-kicker--no-status">
            {t("about.about-capsule")}
          </span>
          <h1 className="h1--xl-condensed">{t("about.we-make-crm-simple")}</h1>
          <p className="p--lg">{t("about.value-of-modern-crm")}</p>
        </div>
      </section>

      <section className="container-fluid row center-xs">
        <div className="col-xs-12">
          <div className="about-page__video-wrapper">
            <WistiaVideoEmbed
              className="about-page__team-video-image"
              videoId="lb2mu82p2n"
              videoType="popup"
              videoStyle="branded"
              posterImage="/about/video-image.jpg"
              showPlayButton="false"
              replaceThumbnail={true}
            />
            <div className="about-page__play-button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.5 5.65302C4.5 4.22602 6.029 3.32302 7.279 4.01002L18.819 10.357C20.114 11.069 20.114 12.93 18.819 13.643L7.28 19.99C6.03 20.677 4.501 19.773 4.501 18.347L4.5 5.65302Z"
                  fill="#0A0A0A"
                />
              </svg>
              <span className="about-page__play-button-text">
                {t("about.watch-the-video")}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="container-fluid row center-xs top-xs text-left mb-0">
        <div className="col-xs-12 col-md-6">
          <h2 className="mb-5">{t("about.simplifying-crm")}</h2>

          <p className="mb-8 p--lg">{t("about.founder-frustration")}</p>
          <p className="mb-8 p--lg">{t("about.built-to-deliver-on-values")}</p>
          <p className="p--lg">{t("about.manchester-based")}</p>
        </div>
        <Divider />
      </section>

      <FeatureTileGrid title={t("about.values.title")} alignLeft={true}>
        <FeatureTile
          icon={LuMessageSquareQuote}
          title={t("about.values.integrity.title")}
          description={t("about.values.integrity.description")}
        />
        <FeatureTile
          icon={LuUsersRound}
          title={t("about.values.open.title")}
          description={t("about.values.open.description")}
        />
        <FeatureTile
          icon={LuHeartHandshake}
          title={t("about.values.trust.title")}
          description={t("about.values.trust.description")}
        />
        <FeatureTile
          icon={LuPartyPopper}
          title={t("about.values.celebrate-success.title")}
          description={t("about.values.celebrate-success.description")}
        />
        <FeatureTile
          icon={LuHandHeart}
          title={t("about.values.learn-together.title")}
          description={t("about.values.learn-together.description")}
        />
      </FeatureTileGrid>

      <section className="container-fluid row center-xs">
        <Divider noMargin />

        <div className="col-xs-12 mt-20">
          <div
            className="about-page__image-grid"
            ref={imageGrid}
            data-animate={isIntersectingImageGrid ? "true" : "false"}
          >
            <div className="about-page__image-grid-child">
              <GatsbyImage
                image={getImage(findImage(data.aboutImages, "team-image-1"))}
                alt={t("about.alt.team-image-1")}
              />
            </div>

            <div className="about-page__image-grid-child">
              <GatsbyImage
                image={getImage(findImage(data.aboutImages, "team-image-2"))}
                alt={t("about.alt.team-image-2")}
              />
            </div>

            <div className="about-page__image-grid-child">
              <GatsbyImage
                image={getImage(findImage(data.aboutImages, "team-image-3"))}
                alt={t("about.alt.team-image-3")}
              />
            </div>

            <div className="about-page__image-grid-child">
              <GatsbyImage
                image={getImage(findImage(data.aboutImages, "team-image-4"))}
                alt={t("about.alt.team-image-4")}
              />
            </div>

            <div className="about-page__image-grid-child">
              <GatsbyImage
                image={getImage(findImage(data.aboutImages, "team-image-5"))}
                alt={t("about.alt.team-image-5")}
              />
            </div>

            <div className="about-page__image-grid-child">
              <GatsbyImage
                image={getImage(findImage(data.aboutImages, "team-image-6"))}
                alt={t("about.alt.team-image-6")}
              />
            </div>
          </div>
        </div>
        <Divider width="140" noMargin className="mt-20" />
      </section>

      <section className="container-fluid row center-xs">
        <div className="col-xs-12">
          <h2>{t("about.meet-the-people")}</h2>

          <TeamMembers members={teamMembers} images={data.teamImages} />
        </div>
      </section>

      <CalloutBox
        title={t("about.work-with-us.title")}
        description={t("about.work-with-us.description")}
        link={{ to: "/careers", text: t("about.work-with-us.button") }}
        image={getImage(
          findImage(data.aboutImages, "capsule-team-collage"),
        )}
        imageAlt={t("about.work-with-us.alt")}
      />
    </MainLayout>
  )
}

const TeamMemberIcon = ({ icon, memberName }) => {
  let title
  let iconComponent

  if (icon.icon === "twitter") {
    title = `Follow ${memberName} on Twitter`
    iconComponent = (
      <SocialIconTwitter
        className="about-page__team-members-icon"
        width="24px"
        height="24px"
        fill="#fff"
      />
    )
  } else if (icon.icon === "linkedin") {
    title = `Connect with ${memberName} on LinkedIn`
    iconComponent = (
      <SocialIconLinkedIn
        className="about-page__team-members--icon"
        width="24px"
        height="24px"
        fill="#fff"
      />
    )
  } else if (icon.icon === "github") {
    title = `Follow ${memberName} on Github`
    iconComponent = (
      <SocialIconGithub
        className="about-page__team-members-icon"
        width="24px"
        height="24px"
        fill="#fff"
      />
    )
  } else if (icon.icon === "dribbble") {
    title = `Follow ${memberName} on Dribbble`
    iconComponent = (
      <SocialIconDribbble
        className="about-page__team-members-icon"
        width="24px"
        height="24px"
        fill="#fff"
      />
    )
  } else {
    title = `Visit ${memberName}'s website`
    iconComponent = (
      <SocialIconWebsite
        className="about-page__team-members-icon"
        width="24px"
        height="24px"
        fill="#fff"
      />
    )
  }

  return (
    <a target="_blank" href={icon.url} title={title}>
      {iconComponent}
    </a>
  )
}

const TeamMemberIcons = ({ member }) => {
  return (
    <div className="about-page__team-members-icons">
      {member.icons.map(icon => (
        <TeamMemberIcon key={icon.icon} icon={icon} memberName={member.name} />
      ))}
    </div>
  )
}

const TeamMembers = ({ members, images }) => {
  return (
    <ul className="about-page__team-members mt-10 sm:mt-16">
      {members.map(member => (
        <li
          key={member.name}
          className={`about-page__team-members-item ${
            member.icons ? "about-page__team-members--icons" : ""
          } mb-12`}
        >
          <div className="about-page__team-members-avatar mr-5 mb-4 ml-5">
            <GatsbyImage
              image={getImage(findImage(images, member.photo))}
              alt={`A headshot of ${member.name}`}
              style={{ width: "100%" }}
            />

            {member.icons && <TeamMemberIcons member={member} />}
          </div>

          <p className="p--sm about-page__team-members-details">
            <span className="h5">{member.name}</span>
            <br />
            <span className="about-page__team-members-role">{member.role}</span>
          </p>
        </li>
      ))}
    </ul>
  )
}

export default AboutPage

export const query = graphql`
  query {
    ...Locales

    teamMembers: allTeamYaml {
      edges {
        node {
          name
          role
          photo
          icons {
            url
            icon
          }
        }
      }
    }

    teamImages: allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "team" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }

    aboutImages: allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "about" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`
